declare global {
  interface Window {
    __INITIALSTATE__: Record<string, any>;
  }
}

/*
 * This class reads initial global state. It is used both to hydrate modules/reducers
 * and for accessing immutable, read-only state data directly in components.
 */
class InitialState {
  data;
  algolia;
  lenders;
  locations;
  regions;
  prices;
  programs;
  policies;
  altLenders;
  loanOptions;
  propertyTypes;
  lendersById;
  locationsById;
  regionsById;

  constructor() {
    this.data = window.__INITIALSTATE__ ?? {};
    const lenders = this.data.lenders ?? [];
    const prices = this.data.prices ?? [];
    const locations = this.data.locations ?? [];
    const regions = this.data.regions ?? [];

    this.algolia = this.data.config?.algolia ?? {};

    this.lenders = lenders;
    this.locations = locations;
    this.regions = regions;

    this.prices = {
      forUsers: prices.filter((price: { for: string }) => price.for === 'users'),
      forProUsers: prices.filter((price: { for: string }) => price.for === 'pro-users'),
    };

    this.programs = this.data.programs ?? [];
    this.policies = this.data.policies ?? [];

    // Alternative lending
    this.altLenders = this.normalizeData(this.data.alt_lenders ?? []);
    this.loanOptions = {
      commercial: this.normalizeData(this.data.commercial_loan_options ?? []),
      construction: this.normalizeData(this.data.construction_loan_options ?? []),
      residential: this.normalizeData(this.data.residential_loan_options ?? []),
    };
    this.propertyTypes = {
      commercial: this.normalizeData(this.data.commercial_property_types ?? []),
      construction: this.normalizeData(this.data.construction_property_types ?? []),
      residential: this.normalizeData(this.data.residential_property_types ?? []),
    };

    /**
     * Retrieves normalized lender data from initial state.
     * @var {Object}
     */
    this.lendersById = lenders.reduce(
      (accum: Record<string, unknown>, lender: { id: string | number }) => ({
        ...accum,
        [lender.id]: lender,
      }),
      {}
    );

    /**
     * Retrieves normalized location data from initial state.
     * @var {Object}
     */
    this.locationsById = locations.reduce(
      (
        accum: Record<string, unknown>,
        location: { id: string | number; name: string; region_id: string | number }
      ) => ({
        ...accum,
        [location.id]: {
          label: location.name,
          region: location.region_id,
          value: location.id,
        },
      }),
      {}
    );

    /**
     * Retrieves normalized province data from initial state.
     * @var {Object}
     */
    this.regionsById = regions.reduce(
      (accum: Record<string, unknown>, region: { id: string | number; name: string }) => ({
        ...accum,
        [region.id]: {
          label: region.name,
          value: region.id,
        },
      }),
      {}
    );
  }

  /**
   * Helper for normalizing initial state data.
   * @param {Object} initialData
   * @return {Object}
   */
  normalizeData = (initialData: any[]) =>
    initialData?.reduce(
      (accum, type) => ({
        ...accum,
        [type.id]: {
          label: type.name,
          value: type.id,
        },
      }),
      {}
    );
}

export default new InitialState();
